import type { MsalService } from '@azure/msal-angular';
import { BrowserAuthError, ClientConfigurationError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { firstValueFrom } from 'rxjs';

export async function initializeMsalSession(msalService: MsalService, scopes: string[]): Promise<void> {
    if (window !== window.parent && !window.opener) return;

    const instance = msalService.instance;
    // Handle any redirect response from MSAL before continuing with session initialization.
    const redirectResult = await firstValueFrom(msalService.handleRedirectObservable({ navigateToLoginRequestUrl: false }));
    if (window.location.pathname.replace(/\/+$/, '') === '/signed-out') {
        // The public logout landing page must not silently start another session.
        return;
    }

    if (redirectResult?.account) {
        instance.setActiveAccount(redirectResult.account);
    }

    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) return;

    instance.setActiveAccount(account);

    try {
        await instance.acquireTokenSilent({ account, scopes: [...scopes] });
    } catch (error) {
        if (
            error instanceof InteractionRequiredAuthError ||
            (error instanceof ClientConfigurationError && error.errorCode === 'authority_mismatch') ||
            (error instanceof BrowserAuthError && error.errorCode === 'timed_out' && error.subError === 'redirect_bridge_timeout')
        ) {
            // Let the usual route guard start redirect login when silent recovery is unavailable.
            await instance.clearCache();
            return;
        }

        throw error;
    }
}
